import React, { useState, useEffect, useContext } from "react";
import { API, AuthContext } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { Users, Clock, Copy, RefreshCw } from "lucide-react";

const STATUS_CLS = {
  pending: "bg-amber-100 text-amber-700",
  approved: "bg-green-100 text-green-700",
  auto_approved: "bg-blue-100 text-blue-700",
  rejected: "bg-rose-100 text-rose-700",
};

const fmtCountdown = (secs) => {
  if (secs == null) return "—";
  if (secs <= 0) return "due now";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${s}s`;
};

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";

export default function DemoAdmin() {
  const { user } = useContext(AuthContext);
  const [tab, setTab] = useState("requests");
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [metrics, setMetrics] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [extendDays, setExtendDays] = useState({});

  const token = () => localStorage.getItem("token");
  const headers = () => ({ Authorization: `Bearer ${token()}` });
  const isAdmin = user?.role === "admin";

  const loadAll = async () => {
    const [r, u, m, n] = await Promise.all([
      fetch(`${API}/admin/demo-requests`, { headers: headers() }),
      fetch(`${API}/admin/demo-users`, { headers: headers() }),
      fetch(`${API}/admin/demo-metrics`, { headers: headers() }),
      fetch(`${API}/admin/demo-notifications`, { headers: headers() }),
    ]);
    if (r.ok) setRequests(await r.json());
    if (u.ok) setUsers(await u.json());
    if (m.ok) setMetrics(await m.json());
    if (n.ok) setNotifications(await n.json());
  };

  useEffect(() => {
    if (isAdmin) loadAll();
  }, []); // eslint-disable-line

  // live countdown tick
  useEffect(() => {
    const id = setInterval(() => {
      setRequests((prev) =>
        prev.map((r) =>
          r.status === "pending" && r.seconds_remaining != null
            ? { ...r, seconds_remaining: Math.max(0, r.seconds_remaining - 1) }
            : r
        )
      );
    }, 1000);
    return () => clearInterval(id);
  }, []);

  const act = async (id, action) => {
    const res = await fetch(`${API}/admin/demo-requests/${id}/${action}`, { method: "POST", headers: headers() });
    if (res.ok) {
      toast.success(`Request ${action}d`);
      loadAll();
    } else {
      const e = await res.json().catch(() => ({}));
      toast.error(e.detail || "Action failed");
    }
  };

  const extend = async (id) => {
    const days = parseInt(extendDays[id] || "7", 10);
    const res = await fetch(`${API}/admin/demo-users/${id}/extend`, {
      method: "POST",
      headers: { ...headers(), "Content-Type": "application/json" },
      body: JSON.stringify({ days }),
    });
    if (res.ok) {
      toast.success(`Extended by ${days} days`);
      loadAll();
    } else toast.error("Could not extend");
  };

  const convert = async (id) => {
    const res = await fetch(`${API}/admin/demo-users/${id}/convert`, { method: "POST", headers: headers() });
    if (res.ok) {
      toast.success("Converted to paid");
      loadAll();
    } else toast.error("Could not convert");
  };

  const copyLink = (path) => {
    navigator.clipboard.writeText(`${ORIGIN}${path}`);
    toast.success("Link copied");
  };

  const sendWhatsApp = (r) => {
    if (!r.access_link) return;
    const digits = (r.phone || "").replace(/\D/g, "");
    const intl = digits.startsWith("0") ? "61" + digits.slice(1) : digits;
    const msg = `Hi ${r.first_name}, your SiteProApp trial is approved! Tap to set your password and log in: ${ORIGIN}${r.access_link}`;
    window.open(`https://wa.me/${intl}?text=${encodeURIComponent(msg)}`, "_blank");
  };

  if (!isAdmin) {
    return <div className="p-8 text-center text-slate-500" data-testid="demo-admin-denied">Admin access only.</div>;
  }

  const Stat = ({ label, value, testid }) => (
    <Card>
      <CardContent className="py-4">
        <p className="text-2xl font-bold text-slate-900" data-testid={testid}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </CardContent>
    </Card>
  );

  return (
    <div className="max-w-6xl mx-auto p-4 sm:p-6" data-testid="demo-admin-page">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-amber-600 flex items-center justify-center">
            <Users className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Demo & Trials</h1>
            <p className="text-sm text-slate-500">Approve requests, manage trials, track conversion</p>
          </div>
        </div>
        <Button variant="outline" onClick={loadAll} data-testid="demo-admin-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {metrics && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <Stat label="Requests" value={metrics.requests_submitted} testid="metric-requests" />
          <Stat label="Active Trials" value={metrics.active_trials} testid="metric-active" />
          <Stat label="Expired" value={metrics.expired} testid="metric-expired" />
          <Stat label="Converted" value={metrics.converted} testid="metric-converted" />
          <Stat label="Conversion %" value={`${metrics.conversion_rate}%`} testid="metric-rate" />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="requests" data-testid="demo-tab-requests">Requests</TabsTrigger>
          <TabsTrigger value="trials" data-testid="demo-tab-trials">Trials</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="demo-tab-notifications">Notifications</TabsTrigger>
        </TabsList>

        <TabsContent value="requests" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Phone</th>
                    <th className="p-3">Company</th><th className="p-3">Status</th>
                    <th className="p-3">Time Left</th><th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {requests.length === 0 && (
                    <tr><td colSpan={7} className="p-6 text-center text-slate-400">No demo requests yet.</td></tr>
                  )}
                  {requests.map((r) => (
                    <tr key={r.id} className="border-t" data-testid={`demo-request-row-${r.id}`}>
                      <td className="p-3">{r.first_name} {r.last_name}</td>
                      <td className="p-3">{r.email}</td>
                      <td className="p-3">{r.phone}</td>
                      <td className="p-3">{r.company || "—"}</td>
                      <td className="p-3"><Badge className={`${STATUS_CLS[r.status]} border-0`}>{r.status}</Badge></td>
                      <td className="p-3">
                        {r.status === "pending" ? (
                          <span className="flex items-center gap-1 text-amber-700"><Clock className="w-3 h-3" />{fmtCountdown(r.seconds_remaining)}</span>
                        ) : "—"}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end gap-2">
                          {r.status === "pending" ? (
                            <>
                              <Button size="sm" className="bg-green-600 hover:bg-green-700 h-8" onClick={() => act(r.id, "approve")} data-testid={`demo-approve-${r.id}`}>Approve</Button>
                              <Button size="sm" variant="outline" className="h-8 text-rose-600" onClick={() => act(r.id, "reject")} data-testid={`demo-reject-${r.id}`}>Reject</Button>
                            </>
                          ) : r.access_link ? (
                            <>
                              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 h-8" onClick={() => copyLink(r.access_link)} title="Copy the account-setup link to send the customer" data-testid={`demo-copy-customer-${r.id}`}>
                                <Copy className="w-3 h-3 mr-1" /> Customer link
                              </Button>
                              <Button size="sm" variant="outline" className="h-8 text-green-700 border-green-300 hover:bg-green-50" onClick={() => sendWhatsApp(r)} title="Send the setup link via WhatsApp" data-testid={`demo-whatsapp-${r.id}`}>
                                WhatsApp
                              </Button>
                            </>
                          ) : (
                            <span className="text-xs text-slate-400">{r.password_set ? "Account active" : "—"}</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="trials" className="mt-4">
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr>
                    <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">Day</th>
                    <th className="p-3">Site?</th><th className="p-3">Status</th><th className="p-3 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length === 0 && (
                    <tr><td colSpan={6} className="p-6 text-center text-slate-400">No demo users yet.</td></tr>
                  )}
                  {users.map((u) => (
                    <tr key={u.id} className="border-t" data-testid={`demo-user-row-${u.id}`}>
                      <td className="p-3">{u.username}</td>
                      <td className="p-3">{u.email}</td>
                      <td className="p-3">Day {u.day_of_trial} of 30</td>
                      <td className="p-3">{u.site_created ? "Yes" : "No"}</td>
                      <td className="p-3">
                        {u.demo_converted ? <Badge className="bg-green-100 text-green-700 border-0">Converted</Badge>
                          : u.expired ? <Badge className="bg-rose-100 text-rose-700 border-0">Expired</Badge>
                          : <Badge className="bg-blue-100 text-blue-700 border-0">Active</Badge>}
                      </td>
                      <td className="p-3">
                        <div className="flex justify-end items-center gap-2">
                          <Input className="w-16 h-8" type="number" value={extendDays[u.id] ?? "7"} onChange={(e) => setExtendDays((p) => ({ ...p, [u.id]: e.target.value }))} data-testid={`demo-extend-days-${u.id}`} />
                          <Button size="sm" variant="outline" className="h-8" onClick={() => extend(u.id)} data-testid={`demo-extend-${u.id}`}>Extend</Button>
                          {!u.demo_converted && (
                            <Button size="sm" className="h-8 bg-amber-600 hover:bg-amber-700" onClick={() => convert(u.id)} data-testid={`demo-convert-${u.id}`}>Convert</Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <p className="text-xs text-slate-500 mb-3">
            Emails &amp; SMS are simulated until a provider is connected. The <span className="font-semibold text-amber-700">"You're in" (email_access_granted)</span> message contains the link to send the customer so they can set a password and log in.
          </p>
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-500 text-left">
                  <tr><th className="p-3">Type</th><th className="p-3">To</th><th className="p-3">Subject</th><th className="p-3">When</th><th className="p-3 text-right">Link</th></tr>
                </thead>
                <tbody>
                  {notifications.length === 0 && (
                    <tr><td colSpan={5} className="p-6 text-center text-slate-400">No notifications yet.</td></tr>
                  )}
                  {notifications.map((n) => {
                    const isCustomerLink = n.kind === "email_access_granted";
                    return (
                    <tr key={n.id} className="border-t" data-testid={`demo-notif-row-${n.id}`}>
                      <td className="p-3 font-mono text-xs">{n.kind}</td>
                      <td className="p-3">{n.to}</td>
                      <td className="p-3 max-w-[260px] truncate">{n.subject}</td>
                      <td className="p-3 whitespace-nowrap">{new Date(n.created_at).toLocaleString("en-AU")}</td>
                      <td className="p-3 text-right">
                        {n.link ? (
                          <Button size="sm" variant={isCustomerLink ? "default" : "ghost"} className={`h-8 ${isCustomerLink ? "bg-amber-600 hover:bg-amber-700" : ""}`} onClick={() => copyLink(n.link)} data-testid={`demo-notif-copy-${n.id}`}>
                            <Copy className="w-3 h-3 mr-1" /> {isCustomerLink ? "Customer link" : "Copy"}
                          </Button>
                        ) : "—"}
                      </td>
                    </tr>
                  );})}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
